import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Gap from './Gap'
import Avatar from './Avatar'
import SheetHeader from './SheetHeader'
import LoadingSpinner from './LoadingSpinner'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'

export interface SheetPerson {
  userId: string
  username?: string | null
  nickname: string | null
  profileImage: string | null
}

interface PeopleBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title: string
  /** null 이면 불러오는 중 */
  people: SheetPerson[] | null
}

/**
 * 사람 목록 바텀시트. 기록의 '태그된 친구' 시트(FeedReviewBox)와 **같은 모양**이다 —
 * 손잡이 · 공용 헤더 · 사진 48px · 아이디(굵게)/닉네임 두 줄 · 누르면 프로필.
 * 2026-11-04 친구 초대 화면의 '+N' 에 쓰려고 꺼냈다. 피드 쪽은 아직 자기 코드를 쓴다.
 */
const PeopleBottomSheet: React.FC<PeopleBottomSheetProps> = ({ isOpen, onClose, title, people }) => {
  const navigate = useNavigate()
  const { handleProps, sheetStyle } = useSheetDragDismiss({ onClose, enabled: isOpen })

  // 열려 있는 동안 뒤 화면이 따라 스크롤되지 않게
  useEffect(() => {
    if (!isOpen) return
    document.body.style.overflow = 'hidden'
    document.body.style.touchAction = 'none'
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-[1050]" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center pointer-events-none">
        <div className="app-container flex flex-col pointer-events-auto">
          <div
            className="bg-ivoryKey rounded-t-30px max-h-[60vh] flex flex-col overflow-hidden w-full"
            onClick={(e) => e.stopPropagation()}
            style={sheetStyle}
          >
            <div className="flex flex-col w-full cursor-grab" {...handleProps}>
              <Gap height={24} />
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto" />
              <Gap height={12} />
            </div>
            <SheetHeader title={title} />
            <div className="flex-1 overflow-y-auto pb-12px">
              {people === null ? (
                <div className="py-40px flex justify-center">
                  <LoadingSpinner />
                </div>
              ) : (
                people.map((p) => (
                  <div
                    key={p.userId}
                    className="flex items-center gap-12px px-20px py-12px cursor-pointer"
                    onClick={() => {
                      onClose()
                      navigate(`/user/${p.userId}/profile`)
                    }}
                  >
                    <Avatar nickname={p.nickname ?? ''} profileImage={p.profileImage || null} size={48} />
                    <div className="flex flex-col">
                      <span className="font-pretendard font-bold text-14px text-textColorText2">
                        {p.username || p.nickname}
                      </span>
                      <span className="font-pretendard font-regular text-14px text-textColorText2">
                        {p.nickname}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PeopleBottomSheet
